import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";
import SiteGate from "@/components/SiteGate";
import TrackingPage from "@/pages/TrackingPage";
import AuthPage from "@/pages/AuthPage";
import AuthCallback from "@/pages/AuthCallback";
import AdminDashboard from "@/pages/AdminDashboard";
import CustomerPortal from "@/pages/CustomerPortal";
import AboutPage from "@/pages/AboutPage";
import PortfolioPage from "@/pages/PortfolioPage";
import ContactPage from "@/pages/ContactPage";
import InfoPage from "@/pages/InfoPage";
import "@/App.css";

function FullLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0B0F17]">
      <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
    </div>
  );
}

function Protected({ children, role }) {
  const { user, loading } = useAuth();
  const denied = user && role && user.role !== role;

  useEffect(() => {
    if (denied) toast.error("Access denied — you do not have permission to view that page.");
  }, [denied]);

  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (denied) {
    return <Navigate to={user.role === "admin" ? "/admin" : "/my-shipments"} replace />;
  }
  return children;
}

function AppRouter() {
  const location = useLocation();
  // Detect Google OAuth return during render (prevents race conditions)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<TrackingPage />} />
      <Route path="/login" element={<AuthPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/portfolio" element={<PortfolioPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/info/:slug" element={<InfoPage />} />
      <Route path="/admin" element={<Protected role="admin"><AdminDashboard /></Protected>} />
      <Route path="/my-shipments" element={<Protected role="customer"><CustomerPortal /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <SiteGate>
        <AuthProvider>
          <BrowserRouter>
            <AppRouter />
          </BrowserRouter>
          <Toaster position="top-right" richColors theme="dark" />
        </AuthProvider>
      </SiteGate>
    </div>
  );
}
